import { useEffect, useRef, useState } from "react";
import Icon from "../../Icon/Icon.jsx";
import MobileMenuContextLabelButton from "../MobileMenu/MobileMenuContextLabelButton.jsx";
import cx from "classnames";
import { func, string, bool, object } from "prop-types";

const KEYCODE_TAB = 9;

function useFocusLock(props) {
  const { groupRef } = props;
  let firstElement;
  let lastElement;

  if (groupRef.current) {
    const focusableModalElements = groupRef.current.querySelectorAll(
      "a[href], button:not([disabled]), textarea, input, select",
    );

    firstElement = focusableModalElements[0];
    lastElement = focusableModalElements[focusableModalElements.length - 1];
  }

  const handleForLastInList = (e) => {
    if (e.key === "Tab" || e.keyCode === KEYCODE_TAB) {
      if (!e.shiftKey) {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    }
  };

  return { handleForLastInList };
}

const propTypes = {
  item: object,
  granchildMenuOpenLabel: string,
  setGrandchildMenuOpenLabel: func,
  isDesktop: bool,
  menuItemDimensions: object,
};

const GrandChildItem = (props) => {
  const {
    item: { href, title, label, target, comingSoon, comingSoonLabel, disabled },
    ...otherProps
  } = props;

  if (!disabled) {
    // Active status for Narrative Page
    return (
      <a
        href={href}
        target={target}
        className="featured-content__nested-panel-grandchild-link"
        {...otherProps}
      >
        {label}
      </a>
    );
  } else if (comingSoon) {
    // Coming Soon status
    return (
      <span className="featured-content__nested-panel-grandchild-link featured-content__nested-panel-grandchild-span">
        <span>{label}</span>
        <span className="featured-content__coming-soon">{comingSoonLabel}</span>
      </span>
    );
  } else {
    // Inactive status
    return null;
  }
};

const LinkGroup = (props) => {
  const grandchildRef = useRef();
  const { handleForLastInList } = useFocusLock({ groupRef: grandchildRef });
  const {
    item,
    granchildMenuOpenLabel,
    setGrandchildMenuOpenLabel,
    isDesktop,
    menuItemDimensions,
  } = props;

  const [isThisPanelOpening, setIsThisPanelOpening] = useState(false);

  const sanitizedLabel = document
    .createTextNode(item.label)
    .textContent.replaceAll(" ", "");
  const panelContainerId = "panelContainer-" + sanitizedLabel;

  const togglePanel = (label) => {
    setIsThisPanelOpening(label === item.label);
    setGrandchildMenuOpenLabel(label);
  };

  useEffect(() => {
    if (!isThisPanelOpening) {
      return;
    }

    const panel = document.getElementById(panelContainerId);
    panel?.focus();
  });

  const returnToPreviousScreen = () => {
    setGrandchildMenuOpenLabel("");
  };

  const closePanel = ({ currentTarget, relatedTarget }) => {
    if (currentTarget.contains(relatedTarget)) {
      return;
    }

    togglePanel("");
  };

  return (
    <li
      className={cx("featured-content__child-list-item", {
        "featured-content__child-list-item--open":
          granchildMenuOpenLabel === item.label,
      })}
    >
      <button
        type="button"
        className={cx("featured-content__child-button", {
          "featured-content__child-button--open":
            granchildMenuOpenLabel === item.label,
        })}
        onClick={() => togglePanel(item.label)}
      >
        {item.label}
        <div className="featured-content__child-button-svg-container">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.2091 8.49657C10.2493 8.4524 10.2848 8.40304 10.3096 8.34783C10.3699 8.23741 10.4 8.12179 10.4 8.00032C10.4 7.87886 10.3699 7.76259 10.3143 7.65737C10.2889 7.60281 10.2541 7.5528 10.2145 7.50278L7.14967 4.14079C6.97888 3.95307 6.7029 3.95307 6.53211 4.14079C6.356 4.3285 6.356 4.63248 6.53211 4.8202L9.41579 8L6.53211 11.1804C6.356 11.3682 6.356 11.6715 6.53211 11.8592C6.7029 12.0469 6.97888 12.0469 7.14967 11.8592L10.2091 8.49657Z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="0.25"
            />
          </svg>
        </div>
      </button>

      {isDesktop ? (
        <div
          className={cx("featured-content__nested-panel", {
            "featured-content__nested-panel--open":
              granchildMenuOpenLabel === item.label,
          })}
        >
          <div>{/* Purposely kept empty to create a grid column */}</div>
          <div
            className="featured-content__nested-panel-list-container"
            id={panelContainerId}
            tabIndex="-1"
            onBlur={closePanel}
          >
            {item.children && item.children.length && (
              <div className="featured-content__nested-panel-list">
                <div className="featured-content__nested-panel-list-item">
                  <div className="featured-content__nested-panel-list-label">
                    {item.children[0].label}
                  </div>
                  <ul>
                    {item.children[0].children.map((grandchild, l) => {
                      return (
                        <li
                          key={l}
                          className="featured-content__nested-panel-grandchild-item"
                        >
                          <GrandChildItem item={grandchild} />
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className="featured-content__nested-panel-list-item">
                  {item.children.slice(1).map((child, k) => {
                    return (
                      <div
                        key={k}
                        className="featured-content__nested-panel-list__right-col-child"
                      >
                        <div className="featured-content__nested-panel-list-label">
                          {child.label}
                        </div>
                        <ul>
                          {child.children.map((grandchild, l) => {
                            return (
                              <li
                                key={l}
                                className="featured-content__nested-panel-grandchild-item"
                              >
                                <GrandChildItem item={grandchild} />
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <button
              type="button"
              className="featured-content__nested-panel-close-button"
              onClick={closePanel}
              aria-label={`Close ${item.label} menu`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M20.1782 19.631C20.2539 19.7083 20.3207 19.7947 20.3675 19.8913C20.4811 20.0845 20.5379 20.2869 20.5379 20.4994C20.5379 20.712 20.4811 20.9155 20.3764 21.0996C20.3285 21.1951 20.2628 21.2826 20.1882 21.3701L14.4127 27.2536C14.0908 27.5821 13.5708 27.5821 13.2489 27.2536C12.917 26.9251 12.917 26.3932 13.2489 26.0646L18.6831 20.5L13.2489 14.9342C12.917 14.6057 12.917 14.0749 13.2489 13.7464C13.5708 13.4179 14.0908 13.4179 14.4127 13.7464L20.1782 19.631Z"
                  fill="#00416B"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.8218 21.369C19.7461 21.2917 19.6793 21.2053 19.6325 21.1087C19.5189 20.9155 19.4621 20.7131 19.4621 20.5006C19.4621 20.288 19.5189 20.0845 19.6236 19.9004C19.6715 19.8049 19.7372 19.7174 19.8118 19.6299L25.5873 13.7464C25.9092 13.4179 26.4292 13.4179 26.7511 13.7464C27.083 14.0749 27.083 14.6068 26.7511 14.9354L21.3169 20.5L26.7511 26.0658C27.083 26.3943 27.083 26.9251 26.7511 27.2536C26.4292 27.5821 25.9092 27.5821 25.5873 27.2536L19.8218 21.369Z"
                  fill="#00416B"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div
          ref={grandchildRef}
          className={cx(
            "mobile-menu__grandchild-menu mobile-menu__child-menu",
            {
              "mobile-menu__child-menu--open":
                granchildMenuOpenLabel === item.label,
            },
          )}
          style={{
            height: `calc(100vh - ${menuItemDimensions?.height}px)`,
            top: "0",
          }}
        >
          {item.children && item.children.length && (
            <>
              <MobileMenuContextLabelButton
                returnToPreviousScreen={returnToPreviousScreen}
                label={item.label}
              />
              <ul className="featured-content__nested-panel-list mobile__nested-panel-list">
                {item.children.map((child, k) => {
                  return (
                    <li
                      key={k}
                      className="featured-content__nested-panel-list-item"
                    >
                      <div className="featured-content__nested-panel-list-label">
                        {child.label}
                      </div>
                      <ul>
                        {child.children.map((grandchild, l) => {
                          return (
                            <li
                              key={l}
                              className="featured-content__nested-panel-grandchild-item"
                            >
                              <GrandChildItem
                                item={grandchild}
                                onKeyDown={handleForLastInList}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}
    </li>
  );
};

LinkGroup.propTypes = propTypes;
export default LinkGroup;
